import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { ModalNextProvider, Loader } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { error } from '@jutro/logger';
import { isCapabilityEnabled, getNormalizedLOBName } from 'gw-portals-config-js';
import { useAuthentication } from 'gw-digital-auth-react';
import { PolicyService } from 'gw-capability-policy';
import { DocumentService } from 'gw-capability-policydocument';
import {
    PolicyCommonDetails,
    PolicyCommonDocuments,
    PolicyCommonContacts,
    PolicyCoverages
} from 'gw-capability-policy-common-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { withApplicationPropertyContext } from 'tdic-components-platform-react';
import metadata from './PolicyDetails.metadata.json5';
import messages from './PolicyDetails.messages';
import styles from './PolicyDetails.module.scss';

const handlePrint = () => {
    window.print();
};

function PolicyDetails(props) {
    const { history, match, isAuthorisedUser } = props;
    const { authHeader } = useAuthentication();
    const [policyCommonDetails, updatePolicyCommonDetails] = useState({
        documentTypes: [],
        currentPeriodDetails: {},
        renewedPeriodDetails: {},
        hasRenewedPeriod: false
    });
    const [writableDocuments, updateWritableDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const translator = useContext(TranslatorContext);
    const { capabilitiesConfig } = appConfig;
    const policyNumber = _.get(match, 'params.policyNumber');

    const getPolicyDetails = useCallback(() => {
        PolicyService
            .getAccountPolicyDetails(policyNumber, authHeader)
            .then((policyDetails) => {
                updatePolicyCommonDetails({
                    documentTypes: _.get(policyDetails, 'documentTypes', []),
                    currentPeriodDetails: _.get(policyDetails, 'currentPeriod', {}),
                    renewedPeriodDetails: policyDetails.renewedPeriod
                        ? policyDetails.renewedPeriod : {},
                    hasRenewedPeriod: !_.isEmpty(policyDetails.renewedPeriod)
                });
            })
            .catch((err) => {
                error(`PolicyDetails: getPolicyDetails - Unable to get policy details ${JSON.stringify(err)}`);
                ModalNextProvider.showAlert({
                    title: messages.policyRequestFailed,
                    message: messages.CanNotLoadYourPolicyAtThisTime,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.ok
                }).then(() => {
                    history.push('/error');
                }, _.noop);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [policyNumber, authHeader, history]);


    const redirectToEndorsement = useCallback(
        (policyDetails, requestType) => {
            const productCode = policyDetails.offeringCode;
            history.push(`/endorsement-${getNormalizedLOBName(productCode)}`,
                { policyNumber, policyDetails, requestType });
        },
        [history, policyNumber]
    );

    const canChangePolicy = useCallback(
        (policyPeriod) => {
            if (!_.isEmpty(policyPeriod)) {
                const productCode = policyPeriod.offeringCode;
                if (_.isEmpty(productCode)) {
                    return false;
                }
                return (
                    isCapabilityEnabled({
                        capabilitiesConfig,
                        capabilityName: 'policychange',
                        lob: productCode
                    }) && policyPeriod.canBeChanged
                );
            }
            return false;
        },
        [capabilitiesConfig]
    );

    const getPolicyDownloadLink = useCallback(() => {
        return DocumentService.downloadPolicyDocument(
            _.get(policyCommonDetails.currentPeriodDetails, 'idCardPublicID'),
            _.get(policyCommonDetails.currentPeriodDetails, 'idCardSessionID')
        );
    }, [policyCommonDetails]);

    useEffect(
        () => {
            try {
                getPolicyDetails();
            } catch (err) {
                error(`PolicyDetails: useEffect - Unable to fetch policy details ${JSON.stringify(err)}`);
                throw new Error(err);
            }
        },
        // Only need to run once when initial page render and when documents are added/removed
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [writableDocuments.length]
    );

    const overrideProps = {
        pageTitle: {
            content: translator(messages.pageTitle, {
                policyNumber: policyCommonDetails.currentPeriodDetails.policyNumber
            })
        },
        currentPeriod: {
            heading: translator(messages.currentTab)
        },
        policyChangeButtonContainer: {
            visible: canChangePolicy(
                policyCommonDetails.currentPeriodDetails,
                policyCommonDetails.currentPeriodDetails.productName
            ) && !isAuthorisedUser
        },
        idCardContainer: {
            visible: !_.isEmpty(_.get(policyCommonDetails.currentPeriodDetails, 'idCardPublicID'))
        },
        idCard: {
            href: getPolicyDownloadLink(),
            target: '_blank'
        },
        policyDocumentsSection: {
            updateWritableDocuments: updateWritableDocuments,
            writableDocuments: writableDocuments,
            documentTypes: _.get(policyCommonDetails, 'documentTypes', []),
            policyCommonDetails
        }
    };

    const resolvers = {
        resolveComponentMap: {
            policycommondetailscomponent: PolicyCommonDetails,
            policycoveragescomponent: PolicyCoverages,
            policycommondocumentscomponent: PolicyCommonDocuments,
            policycommoncontactscomponent: PolicyCommonContacts
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onPrint: handlePrint,
            onChangePolicy: () => redirectToEndorsement(policyCommonDetails.currentPeriodDetails, 'policyChange'),
            onChangePolicyRenewal: () => redirectToEndorsement(
                policyCommonDetails.renewedPeriodDetails, 'policyRenew'
            )
        }
    };

    return (
        <Loader loaded={!loading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyCommonDetails}
                overrideProps={overrideProps}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </Loader>
    );
}
PolicyDetails.propTypes = {
    match: PropTypes.shape({}).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        productCode: PropTypes.string
    }).isRequired,
    isAuthorisedUser: PropTypes.bool.isRequired
};

export default withRouter(withApplicationPropertyContext(PolicyDetails));
